import _, { each, extend, find, isFunction, noop } from 'underscore';
import $ from 'jquery';

function findIdInToolbarSubMenu(id, toolbarItem) {
  return find(toolbarItem.menu && toolbarItem.menu.items, menuItem => id === menuItem.id);
}

// toolbar item may appear in both selected & unselected, such as inline chart
class ToolbarEditor {
  constructor(options) {
    const { overrideWith, toolbarConfig, updateToolbar } = options;

    extend(this, {
      overrideWith,
      toolbarConfig,
      updateToolbar,
      $,
    });
  }

  update = (value) => {
    const toolbarConfigs = ['toolbarSelectedConfig', 'toolbarUnselectedConfig'];
    const { id } = value;

    let hasAnyToolbarBeenUpdated = false;

    each(toolbarConfigs, (configName) => {
      let hasBeenUpdated = false;
      const toolbarConfig = this.toolbarConfig[configName];
      if (!toolbarConfig) {
        return;
      }

      if (toolbarConfig.has(id)) {
        toolbarConfig.set(id, extend(toolbarConfig.get(id), value));
        hasBeenUpdated = true;
      }

      if (!hasBeenUpdated) {
        toolbarConfig.forEach((config, key) => {
          const subMenu = findIdInToolbarSubMenu(id, config);

          if (subMenu) {
            extend(subMenu, value);
            toolbarConfig.set(key, extend({}, config));
            hasBeenUpdated = true;
          }
        });
      }

      hasAnyToolbarBeenUpdated = hasBeenUpdated || hasAnyToolbarBeenUpdated;
    });

    if (hasAnyToolbarBeenUpdated && isFunction(this.updateToolbar)) {
      this.updateToolbar();
    }
  }

  get(id) {
    const toolbarConfigs = ['toolbarSelectedConfig', 'toolbarUnselectedConfig'];
    let foundConfig = null;

    each(toolbarConfigs, (configName) => {
      if (!foundConfig) {
        if (this.toolbarConfig[configName].has(id)) {
          foundConfig = this.toolbarConfig[configName].get(id);
        }

        if (!foundConfig) {
          this.toolbarConfig[configName].forEach((config) => {
            const subMenu = findIdInToolbarSubMenu(id, config);
            if (subMenu) {
              foundConfig = subMenu;
            }
          });
        }
      }
    });

    return foundConfig;
  }

  overrideWith(view) {
    if (this.overrideWith) {
      // pass plugin form view to tab wrapper
      this.overrideWith(view);
    }
  }

  set(id, value) {
    this.update(_.defaults({ id }, value));
  }

  restoreToolbar() {
    noop();
  }
}

export function getToolbarEditor({ overrideWith, toolbarConfig, updateToolbar }) {
  return new ToolbarEditor({
    overrideWith,
    toolbarConfig,
    updateToolbar,
  });
}

export function getToolbarPlugin({
  overrideWith,
  resetVisibility,
  toolbarConfig,
  resetGraphVisibility,
  resetToolbarMode,
  updateToolbarDynamicSelectedConfig,
  updateToolbar,
}) {
  return {
    plugin: definePlugin => definePlugin('toolbar', ['gridView'], (gridView) => {
      gridView.on('didSelect', () => {
        const selectedRowCount = gridView.selectedItems().length;
        const showToolbarSelected = selectedRowCount > 0;
        const showToolbarUnselected = selectedRowCount === 0;
        resetToolbarMode({
          showToolbarSelected,
          showToolbarUnselected,
        });
        if (showToolbarSelected && isFunction(toolbarConfig.toolbarDynamicSelectedConfigCallback)) {
          const dynamicSelectedConfig = toolbarConfig.toolbarDynamicSelectedConfigCallback({ gridView });
          updateToolbarDynamicSelectedConfig({
            dynamicSelectedConfig,
          });
        }
      });

      const resetVisibilityAndTriggerUpdate = (...options) => {
        resetVisibility(...options);
        gridView.trigger('didUpdate');
      };

      const toolbarEditor = getToolbarEditor({ overrideWith, toolbarConfig, updateToolbar });

      return {
        gridToolbarGroup: {
          toolbarNames: () => [],
          toolbarWithItem: () => toolbarEditor,
          toolbarWithName: () => toolbarEditor,
          hideAllToolbars: () => {
            resetVisibilityAndTriggerUpdate({
              forceHideToolbar: true,
            });
          },
          showAllToolbars: () => {
            resetVisibilityAndTriggerUpdate({
              forceHideToolbar: false,
            });

            if (_.isFunction(resetGraphVisibility)) {
              resetGraphVisibility();
            }
          },
          updateGridView: noop,
          remove: noop,
        },
      };
    }),
  };
}
